import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  CircularProgress,
  Link,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";

import PageTitle from "../../components/Page Component/PageTitle";
import { RootState } from "../../store/Store";
import {
  fetchData,
  FetchDataFailureAction,
  FetchDataSuccessAction,
} from "../../store/actions";
import {
  jiraChartResponseProfile,
  managerAssignmentsUrl,
  projectsUrl,
  projectTimeLoggersUrl,
} from "../../Service/Apis";
import { useCustomToast } from "../../components/Methods/SnackBar";
import ProjectBreachChart from "../../components/jira/ProjectBreach/ProjectBreachChart";
import ProjectBugsChart from "../../components/jira/ProjectBugs/ProjectBugsChart";
import ProjectEffortChart from "../../components/jira/ProjectEffort/ProjectEffortChart";

interface ProjectRow {
  project_id: string;
  project_name: string;
  project_key: string;
  team_name?: string;
  is_active?: boolean;
}

interface TimeLoggerRow {
  employee_id: string;
  employee_name: string | null;
  employee_code: string | null;
  total_hours: number;
}

interface AssignmentRow {
  id: string;
  employee: string;
  employee_name: string | null;
  manager: string | null;
  manager_name: string | null;
  manager_level: string;
  assignment_type: string | null;
  billable: string | null;
  from_date: string | null;
  till_date: string | null;
}

const MANAGER_TYPE_LABELS: Record<string, string> = {
  qa: "QA",
  project: "Project",
  product: "Product",
  reporting: "Reporting",
};

const TYPED_MANAGER_KEYS = ["qa", "project", "product", "reporting"] as const;

type SortOrder = "asc" | "desc";

type TimeLoggerSortKey = "employee_name" | "employee_code" | "total_hours";

type AssignmentSortKey =
  | "employee_name"
  | "manager_name"
  | "manager_level"
  | "assignment_type"
  | "billable"
  | "from_date"
  | "till_date";

type ManagerTableSortKey =
  | "manager_name"
  | "assignment_type"
  | "manager_level"
  | "employee_name";

const headerCellSx = { fontWeight: 700 };

function cmpStr(a: string, b: string, order: SortOrder): number {
  const r = a.localeCompare(b, undefined, { sensitivity: "base" });
  return order === "asc" ? r : -r;
}

function cmpNum(a: number, b: number, order: SortOrder): number {
  const r = a - b;
  return order === "asc" ? r : -r;
}

function parseAssignmentDay(s: string | null): Dayjs | null {
  if (!s) return null;
  const d = dayjs(s);
  return d.isValid() ? d.startOf("day") : null;
}

/** Assignment interval overlaps filter range (inclusive by day). Open till_date → open end. */
function assignmentOverlapsRange(
  row: AssignmentRow,
  rangeStart: Dayjs | null,
  rangeEnd: Dayjs | null
): boolean {
  if (!rangeStart && !rangeEnd) return true;
  const empStart = parseAssignmentDay(row.from_date);
  if (!empStart) return false;
  const empEndRaw = row.till_date ? parseAssignmentDay(row.till_date) : null;
  const empEnd = empEndRaw
    ? empEndRaw.endOf("day")
    : dayjs().add(200, "year").endOf("day");

  const rsMs = rangeStart
    ? rangeStart.startOf("day").valueOf()
    : Number.NEGATIVE_INFINITY;
  const reMs = rangeEnd
    ? rangeEnd.endOf("day").valueOf()
    : Number.POSITIVE_INFINITY;
  const a0 = empStart.startOf("day").valueOf();
  const a1 = empEnd.valueOf();
  return a0 <= reMs && a1 >= rsMs;
}

function assignmentTypeLabel(row: AssignmentRow): string {
  if (!row.assignment_type) return "";
  const t = row.assignment_type;
  return MANAGER_TYPE_LABELS[t] ?? MANAGER_TYPE_LABELS[t.toLowerCase()] ?? t;
}

const ProjectDetailPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const showToast = useCustomToast();
  const selectedRole = useSelector((s: RootState) => s.auth.selectedRole);
  const dispatch = useDispatch() as ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  >;

  const [project, setProject] = useState<ProjectRow | null>(null);
  const [timeLoggers, setTimeLoggers] = useState<TimeLoggerRow[]>([]);
  const [assignments, setAssignments] = useState<AssignmentRow[]>([]);
  const [months, setMonths] = useState("1");
  const [loadingProject, setLoadingProject] = useState(true);
  const [loadingRest, setLoadingRest] = useState(true);

  const [timeSortField, setTimeSortField] = useState<TimeLoggerSortKey | null>(
    null
  );
  const [timeSortOrder, setTimeSortOrder] = useState<SortOrder>("asc");

  const [assignSortField, setAssignSortField] =
    useState<AssignmentSortKey | null>(null);
  const [assignSortOrder, setAssignSortOrder] = useState<SortOrder>("asc");

  const [mgrSortField, setMgrSortField] = useState<ManagerTableSortKey | null>(
    null
  );
  const [mgrSortOrder, setMgrSortOrder] = useState<SortOrder>("asc");

  const [filterBillable, setFilterBillable] = useState<string>("");
  const [filterFromDate, setFilterFromDate] = useState<Dayjs | null>(null);
  const [filterToDate, setFilterToDate] = useState<Dayjs | null>(null);

  const [mgrNameQuery, setMgrNameQuery] = useState("");
  const [mgrTypeFilter, setMgrTypeFilter] = useState<string>("");

  const canAccess = selectedRole === "Admin" || selectedRole === "HR";

  const toggleSort = useCallback(
    <K extends string>(
      field: K,
      currentField: K | null,
      setField: (f: K | null) => void,
      order: SortOrder,
      setOrder: (o: SortOrder) => void
    ) => {
      if (currentField === field) {
        setOrder(order === "asc" ? "desc" : "asc");
      } else {
        setField(field);
        setOrder("asc");
      }
    },
    []
  );

  useEffect(() => {
    if (!canAccess) {
      navigate("/landing", { replace: true });
    }
  }, [canAccess, navigate]);

  const roleParam = useMemo(
    () => ({ role: selectedRole ?? "Employee" }),
    [selectedRole]
  );

  useEffect(() => {
    if (!canAccess || !projectId) return;
    let cancelled = false;
    (async () => {
      setLoadingProject(true);
      try {
        const p = await dispatch(
          fetchData(`${projectsUrl}${projectId}/`, {
            ...jiraChartResponseProfile,
          })
        );
        if (!cancelled) setProject(p as ProjectRow);
      } catch {
        if (!cancelled) {
          showToast("Could not load project", "error", true);
          setProject(null);
        }
      } finally {
        if (!cancelled) setLoadingProject(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canAccess, projectId, dispatch]);

  useEffect(() => {
    if (!canAccess || !projectId) return;
    let cancelled = false;
    (async () => {
      setLoadingRest(true);
      try {
        const [loggers, assigns] = await Promise.all([
          dispatch(
            fetchData(projectTimeLoggersUrl(projectId), {
              ...roleParam,
              ...jiraChartResponseProfile,
              months,
            })
          ),
          dispatch(
            fetchData(managerAssignmentsUrl, {
              ...roleParam,
              ...jiraChartResponseProfile,
              project_id: projectId,
            })
          ),
        ]);
        if (!cancelled) {
          setTimeLoggers(Array.isArray(loggers) ? loggers : []);
          setAssignments(Array.isArray(assigns) ? assigns : []);
        }
      } catch {
        if (!cancelled) {
          showToast("Could not load project details", "error", true);
        }
      } finally {
        if (!cancelled) setLoadingRest(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canAccess, projectId, months, roleParam, dispatch]);

  const managerRowsByType = useMemo(() => {
    const types = new Set<string>(TYPED_MANAGER_KEYS);
    return assignments.filter(
      (a) =>
        a.manager &&
        a.assignment_type &&
        types.has(String(a.assignment_type).toLowerCase())
    );
  }, [assignments]);

  const billableOptions = useMemo(() => {
    const s = new Set<string>();
    assignments.forEach((a) => {
      if (a.billable != null && String(a.billable).trim() !== "") {
        s.add(String(a.billable).trim());
      }
    });
    return [...s].sort((x, y) =>
      x.localeCompare(y, undefined, { sensitivity: "base" })
    );
  }, [assignments]);

  const filteredAssignments = useMemo(() => {
    return assignments.filter((row) => {
      if (filterBillable) {
        const b = row.billable != null ? String(row.billable).trim() : "";
        if (b !== filterBillable) return false;
      }
      if (
        !assignmentOverlapsRange(row, filterFromDate, filterToDate)
      ) {
        return false;
      }
      return true;
    });
  }, [assignments, filterBillable, filterFromDate, filterToDate]);

  const sortedTimeLoggers = useMemo(() => {
    const rows = [...timeLoggers];
    if (!timeSortField) return rows;
    rows.sort((a, b) => {
      switch (timeSortField) {
        case "employee_name":
          return cmpStr(
            (a.employee_name ?? "").toLowerCase(),
            (b.employee_name ?? "").toLowerCase(),
            timeSortOrder
          );
        case "employee_code":
          return cmpStr(
            (a.employee_code ?? "").toLowerCase(),
            (b.employee_code ?? "").toLowerCase(),
            timeSortOrder
          );
        case "total_hours":
          return cmpNum(a.total_hours, b.total_hours, timeSortOrder);
        default:
          return 0;
      }
    });
    return rows;
  }, [timeLoggers, timeSortField, timeSortOrder]);

  const sortedAssignments = useMemo(() => {
    const rows = [...filteredAssignments];
    if (!assignSortField) return rows;

    const valStr = (r: AssignmentRow, key: AssignmentSortKey): string => {
      switch (key) {
        case "employee_name":
          return (r.employee_name ?? r.employee ?? "").toLowerCase();
        case "manager_name":
          return (r.manager_name ?? r.manager ?? "").toLowerCase();
        case "manager_level":
          return (r.manager_level ?? "").toLowerCase();
        case "assignment_type":
          return assignmentTypeLabel(r).toLowerCase();
        case "billable":
          return (r.billable ?? "").toLowerCase();
        case "from_date":
          return r.from_date ?? "";
        case "till_date":
          return r.till_date ?? "";
        default:
          return "";
      }
    };

    rows.sort((a, b) => {
      if (
        assignSortField === "from_date" ||
        assignSortField === "till_date"
      ) {
        const ta = parseAssignmentDay(
          assignSortField === "from_date" ? a.from_date : a.till_date
        );
        const tb = parseAssignmentDay(
          assignSortField === "from_date" ? b.from_date : b.till_date
        );
        const na = ta ? ta.valueOf() : Number.POSITIVE_INFINITY;
        const nb = tb ? tb.valueOf() : Number.POSITIVE_INFINITY;
        return cmpNum(na, nb, assignSortOrder);
      }
      return cmpStr(valStr(a, assignSortField), valStr(b, assignSortField), assignSortOrder);
    });
    return rows;
  }, [filteredAssignments, assignSortField, assignSortOrder]);

  const filteredManagerRows = useMemo(() => {
    const q = mgrNameQuery.trim().toLowerCase();
    return managerRowsByType.filter((row) => {
      if (mgrTypeFilter) {
        if (
          String(row.assignment_type).toLowerCase() !==
          mgrTypeFilter.toLowerCase()
        ) {
          return false;
        }
      }
      if (q) {
        const name = (
          row.manager_name ??
          row.manager ??
          ""
        ).toLowerCase();
        if (!name.includes(q)) return false;
      }
      return true;
    });
  }, [managerRowsByType, mgrNameQuery, mgrTypeFilter]);

  const sortedManagerRows = useMemo(() => {
    const rows = [...filteredManagerRows];
    if (!mgrSortField) return rows;
    rows.sort((a, b) => {
      switch (mgrSortField) {
        case "manager_name":
          return cmpStr(
            (a.manager_name ?? a.manager ?? "").toLowerCase(),
            (b.manager_name ?? b.manager ?? "").toLowerCase(),
            mgrSortOrder
          );
        case "assignment_type":
          return cmpStr(
            assignmentTypeLabel(a).toLowerCase(),
            assignmentTypeLabel(b).toLowerCase(),
            mgrSortOrder
          );
        case "manager_level":
          return cmpStr(
            (a.manager_level ?? "").toLowerCase(),
            (b.manager_level ?? "").toLowerCase(),
            mgrSortOrder
          );
        case "employee_name":
          return cmpStr(
            (a.employee_name ?? a.employee ?? "").toLowerCase(),
            (b.employee_name ?? b.employee ?? "").toLowerCase(),
            mgrSortOrder
          );
        default:
          return 0;
      }
    });
    return rows;
  }, [filteredManagerRows, mgrSortField, mgrSortOrder]);

  const clearAssignmentFilters = () => {
    setFilterBillable("");
    setFilterFromDate(null);
    setFilterToDate(null);
  };

  const clearManagerFilters = () => {
    setMgrNameQuery("");
    setMgrTypeFilter("");
  };

  const hasAssignmentFilters =
    filterBillable !== "" || filterFromDate != null || filterToDate != null;
  const hasManagerFilters = mgrNameQuery.trim() !== "" || mgrTypeFilter !== "";

  if (!canAccess) {
    return null;
  }

  if (!projectId) {
    return (
      <Container sx={{ mt: 12 }}>
        <Typography>Invalid project</Typography>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="xl" sx={{ mt: 12, mb: 10 }}>
        <Button component={RouterLink} to="/projects" sx={{ mb: 2 }}>
          Back to projects
        </Button>
        {loadingProject ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : project ? (
          <Box sx={{ mb: 3 }}>
            <PageTitle>{project.project_name}</PageTitle>
            <Typography color="text.secondary">
              {project.project_key}
              {project.team_name ? ` · ${project.team_name}` : ""}
              {project.is_active === false ? " · Inactive" : ""}
            </Typography>
          </Box>
        ) : (
          <Typography color="error" sx={{ mb: 3 }}>
            Project not found.
          </Typography>
        )}

        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Employees who logged time on this project
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Aggregated Jira worklog hours by employee for the selected period.
          </Typography>
          <ToggleButtonGroup
            size="small"
            value={months}
            exclusive
            onChange={(_, v) => v != null && setMonths(v)}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="1">1M</ToggleButton>
            <ToggleButton value="3">3M</ToggleButton>
            <ToggleButton value="6">6M</ToggleButton>
            <ToggleButton value="12">1Y</ToggleButton>
          </ToggleButtonGroup>
          {loadingRest ? (
            <CircularProgress size={24} />
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>
                    <TableSortLabel
                      active={timeSortField === "employee_name"}
                      direction={
                        timeSortField === "employee_name"
                          ? timeSortOrder
                          : "asc"
                      }
                      onClick={() =>
                        toggleSort(
                          "employee_name",
                          timeSortField,
                          setTimeSortField,
                          timeSortOrder,
                          setTimeSortOrder
                        )
                      }
                    >
                      Employee
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={headerCellSx}>
                    <TableSortLabel
                      active={timeSortField === "employee_code"}
                      direction={
                        timeSortField === "employee_code"
                          ? timeSortOrder
                          : "asc"
                      }
                      onClick={() =>
                        toggleSort(
                          "employee_code",
                          timeSortField,
                          setTimeSortField,
                          timeSortOrder,
                          setTimeSortOrder
                        )
                      }
                    >
                      Code
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sx={headerCellSx}>
                    <TableSortLabel
                      active={timeSortField === "total_hours"}
                      direction={
                        timeSortField === "total_hours"
                          ? timeSortOrder
                          : "asc"
                      }
                      onClick={() =>
                        toggleSort(
                          "total_hours",
                          timeSortField,
                          setTimeSortField,
                          timeSortOrder,
                          setTimeSortOrder
                        )
                      }
                    >
                      Total hours
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {timeLoggers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3}>
                      No time logged in this period.
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedTimeLoggers.map((r) => (
                    <TableRow key={r.employee_id}>
                      <TableCell>
                        <Link
                          component="a"
                          href={`/employeeinformation/${r.employee_id}?months=${months}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover">
                          {r.employee_name ?? "—"}
                        </Link>
                      </TableCell>
                      <TableCell>{r.employee_code ?? "—"}</TableCell>
                      <TableCell align="right">{r.total_hours.toFixed(2)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </Paper>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Employees assigned on this project
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            From active manager assignments where this project is set (assign
            manager form).
          </Typography>
          {loadingRest ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  alignItems: "flex-end",
                  mb: 2,
                }}
              >
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="billable-filter-label">Billable</InputLabel>
                  <Select
                    labelId="billable-filter-label"
                    label="Billable"
                    value={filterBillable}
                    onChange={(e) =>
                      setFilterBillable(e.target.value as string)
                    }
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {billableOptions.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <DatePicker
                  label="From date"
                  value={filterFromDate}
                  onChange={(v) => setFilterFromDate(v)}
                  slotProps={{ textField: { size: "small" } }}
                />
                <DatePicker
                  label="To date"
                  value={filterToDate}
                  onChange={(v) => setFilterToDate(v)}
                  slotProps={{ textField: { size: "small" } }}
                />
                {hasAssignmentFilters && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={clearAssignmentFilters}
                  >
                    Clear filters
                  </Button>
                )}
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>
                      <TableSortLabel
                        active={assignSortField === "employee_name"}
                        direction={
                          assignSortField === "employee_name"
                            ? assignSortOrder
                            : "asc"
                        }
                        onClick={() =>
                          toggleSort(
                            "employee_name",
                            assignSortField,
                            setAssignSortField,
                            assignSortOrder,
                            setAssignSortOrder
                          )
                        }
                      >
                        Employee
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      <TableSortLabel
                        active={assignSortField === "manager_name"}
                        direction={
                          assignSortField === "manager_name"
                            ? assignSortOrder
                            : "asc"
                        }
                        onClick={() =>
                          toggleSort(
                            "manager_name",
                            assignSortField,
                            setAssignSortField,
                            assignSortOrder,
                            setAssignSortOrder
                          )
                        }
                      >
                        Manager
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      <TableSortLabel
                        active={assignSortField === "manager_level"}
                        direction={
                          assignSortField === "manager_level"
                            ? assignSortOrder
                            : "asc"
                        }
                        onClick={() =>
                          toggleSort(
                            "manager_level",
                            assignSortField,
                            setAssignSortField,
                            assignSortOrder,
                            setAssignSortOrder
                          )
                        }
                      >
                        Level
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      <TableSortLabel
                        active={assignSortField === "assignment_type"}
                        direction={
                          assignSortField === "assignment_type"
                            ? assignSortOrder
                            : "asc"
                        }
                        onClick={() =>
                          toggleSort(
                            "assignment_type",
                            assignSortField,
                            setAssignSortField,
                            assignSortOrder,
                            setAssignSortOrder
                          )
                        }
                      >
                        Manager type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      <TableSortLabel
                        active={assignSortField === "billable"}
                        direction={
                          assignSortField === "billable"
                            ? assignSortOrder
                            : "asc"
                        }
                        onClick={() =>
                          toggleSort(
                            "billable",
                            assignSortField,
                            setAssignSortField,
                            assignSortOrder,
                            setAssignSortOrder
                          )
                        }
                      >
                        Billable
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      <TableSortLabel
                        active={assignSortField === "from_date"}
                        direction={
                          assignSortField === "from_date"
                            ? assignSortOrder
                            : "asc"
                        }
                        onClick={() =>
                          toggleSort(
                            "from_date",
                            assignSortField,
                            setAssignSortField,
                            assignSortOrder,
                            setAssignSortOrder
                          )
                        }
                      >
                        From Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      <TableSortLabel
                        active={assignSortField === "till_date"}
                        direction={
                          assignSortField === "till_date"
                            ? assignSortOrder
                            : "asc"
                        }
                        onClick={() =>
                          toggleSort(
                            "till_date",
                            assignSortField,
                            setAssignSortField,
                            assignSortOrder,
                            setAssignSortOrder
                          )
                        }
                      >
                        To Date
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        No active assignments for this project.
                      </TableCell>
                    </TableRow>
                  ) : sortedAssignments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        No assignments match the current filters.
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedAssignments.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell>{r.employee_name ?? r.employee}</TableCell>
                        <TableCell>
                          {r.manager_name ?? r.manager ?? "—"}
                        </TableCell>
                        <TableCell>{r.manager_level}</TableCell>
                        <TableCell>
                          {r.assignment_type
                            ? MANAGER_TYPE_LABELS[r.assignment_type] ??
                              r.assignment_type
                            : "—"}
                        </TableCell>
                        <TableCell>{r.billable ?? "—"}</TableCell>
                        <TableCell>{r.from_date ?? "—"}</TableCell>
                        <TableCell>{r.till_date ?? "—"}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </>
          )}
        </Paper>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Managers by assignment type
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Reporting, project, product, and QA managers linked to this project.
          </Typography>
          {loadingRest ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  alignItems: "flex-end",
                  mb: 2,
                }}
              >
                <TextField
                  size="small"
                  label="Manager name"
                  value={mgrNameQuery}
                  onChange={(e) => setMgrNameQuery(e.target.value)}
                  sx={{ minWidth: 200 }}
                />
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id="mgr-type-filter-label">Type</InputLabel>
                  <Select
                    labelId="mgr-type-filter-label"
                    label="Type"
                    value={mgrTypeFilter}
                    onChange={(e) =>
                      setMgrTypeFilter(e.target.value as string)
                    }
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {TYPED_MANAGER_KEYS.map((k) => (
                      <MenuItem key={k} value={k}>
                        {MANAGER_TYPE_LABELS[k]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {hasManagerFilters && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={clearManagerFilters}
                  >
                    Clear filters
                  </Button>
                )}
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>
                      <TableSortLabel
                        active={mgrSortField === "manager_name"}
                        direction={
                          mgrSortField === "manager_name"
                            ? mgrSortOrder
                            : "asc"
                        }
                        onClick={() =>
                          toggleSort(
                            "manager_name",
                            mgrSortField,
                            setMgrSortField,
                            mgrSortOrder,
                            setMgrSortOrder
                          )
                        }
                      >
                        Manager
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      <TableSortLabel
                        active={mgrSortField === "assignment_type"}
                        direction={
                          mgrSortField === "assignment_type"
                            ? mgrSortOrder
                            : "asc"
                        }
                        onClick={() =>
                          toggleSort(
                            "assignment_type",
                            mgrSortField,
                            setMgrSortField,
                            mgrSortOrder,
                            setMgrSortOrder
                          )
                        }
                      >
                        Type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      <TableSortLabel
                        active={mgrSortField === "manager_level"}
                        direction={
                          mgrSortField === "manager_level"
                            ? mgrSortOrder
                            : "asc"
                        }
                        onClick={() =>
                          toggleSort(
                            "manager_level",
                            mgrSortField,
                            setMgrSortField,
                            mgrSortOrder,
                            setMgrSortOrder
                          )
                        }
                      >
                        Level
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      <TableSortLabel
                        active={mgrSortField === "employee_name"}
                        direction={
                          mgrSortField === "employee_name"
                            ? mgrSortOrder
                            : "asc"
                        }
                        onClick={() =>
                          toggleSort(
                            "employee_name",
                            mgrSortField,
                            setMgrSortField,
                            mgrSortOrder,
                            setMgrSortOrder
                          )
                        }
                      >
                        Employee
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {managerRowsByType.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        No typed manager assignments for this project.
                      </TableCell>
                    </TableRow>
                  ) : sortedManagerRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        No rows match the current filters.
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedManagerRows.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell>{r.manager_name ?? r.manager}</TableCell>
                        <TableCell>
                          {r.assignment_type
                            ? MANAGER_TYPE_LABELS[r.assignment_type] ??
                              r.assignment_type
                            : "—"}
                        </TableCell>
                        <TableCell>{r.manager_level}</TableCell>
                        <TableCell>{r.employee_name ?? r.employee}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </>
          )}
        </Paper>

        {projectId && project && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="h4" sx={{ mb: 2 }}>
              Project charts
            </Typography>
            <ProjectEffortChart projectId={projectId} embedded />
            <ProjectBugsChart projectId={projectId} embedded />
            <ProjectBreachChart projectId={projectId} embedded />
          </Box>
        )}
      </Container>
    </LocalizationProvider>
  );
};

export default ProjectDetailPage;
